import { useMutation, useQuery } from '@apollo/client';
import axios from 'axios';
import {
	and,
	complement,
	equals,
	filter,
	isEmpty,
	isNil,
	map,
	propSatisfies,
	toPairs,
} from 'ramda';
import { useEffect, useState } from 'react';

import { routes } from '@utils/routes';

import { useAuth } from '@providers/AuthProvider';

import { FlowIdTypes, FormData } from '@folx-types/flow';

import CREATE_FLOW_RESPONSES from '@graphql/mutations/createFlowResponses.gql';
import QUERY_SESSION_FLOW_RESPONSES from '@graphql/queries/latestFlowResponsesForSession.gql';
import { ApiEndpoints } from '@utils/endpoints';

interface HookReturnType {
	loaded: boolean;
	error: string;
	formData: FormData;
	submitFormData: (data: FormData) => Promise<void>;
}

const useFlowData = (flowId: FlowIdTypes): HookReturnType => {
	const { loggedIn, userId, initialized: authInitialized } = useAuth();

	const [currentFlowId, setCurrentFlowId] = useState('');
	const [flowData, setFlowData] = useState<FormData>({});
	const [createFlowResponses] = useMutation(CREATE_FLOW_RESPONSES);
	const [responsesHydrated, setResponsesHydrated] = useState(false);
	const [memberSynced, setMemberSynced] = useState(false);
	const [ready, setReady] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const route = routes[flowId];

	const {
		loading: dbDataLoading,
		error: dbDataError,
		data: dbData,
		refetch,
	} = useQuery(QUERY_SESSION_FLOW_RESPONSES, {
		variables: {
			inFlowName: flowId,
		},
		skip: !loggedIn || ready,
	});

	const {
		loading: dataLoading,
		error: dataError,
		data: queryData,
	} = route?.query
		? useQuery(route?.query, {
				skip: !loggedIn || ready,
		  })
		: { loading: false, error: false, data: {} };

	useEffect(() => {
		if (
			!authInitialized ||
			ready ||
			and(complement(isEmpty)(flowId), equals(flowId, currentFlowId))
		) {
			return;
		}

		if (!dbDataLoading || dbDataError) {
			const existingFormData: FormData = flowData;
			dbData?.latestFlowResponsesForCurrentMemberSession?.nodes?.map(
				(n: Record<'key' | 'value', never>) => {
					if (!(n.key in existingFormData)) {
						existingFormData[n.key] = n.value;
					}
				}
			);
			setCurrentFlowId(flowId);
			setFlowData(existingFormData);
			setResponsesHydrated(true);
		}
	}, [dbDataLoading, dbDataError, dbData, ready, flowData, flowId, authInitialized, loggedIn]);

	useEffect(() => {
		if (ready) return;
		if (responsesHydrated && !dataLoading && !!route?.routeData) {
			const existingFormData = route.routeData(flowData, queryData);
			setFlowData(existingFormData);
		}

		if (responsesHydrated && (!dataLoading || dataError)) {
			setMemberSynced(true);
		}
	}, [responsesHydrated, dataLoading, dataError, ready, flowData, queryData, flowId]);

	useEffect(() => {
		if (responsesHydrated && memberSynced) {
			// set up our defaults if not already initialized
			const existingFormData: FormData = flowData;
			existingFormData.storedFiles = existingFormData?.storedFiles ?? [];
			setFlowData(existingFormData);
			setReady(true);
		}
	}, [responsesHydrated, memberSynced, flowData, flowId]);

	const keysToBeUploaded = ['labsImages', 'selfieImage', 'idImage'];

	const submitFormData = async (data: FormData) => {
		if (!userId) {
			throw Error('Unauthorized');
		}
		if (isEmpty(data)) {
			return;
		}

		const flowResponses = filter(
			propSatisfies((x) => !isNil(x), 'value'),
			map(
				(pair) => ({
					key: pair[0],
					value: pair[1],
					memberId: userId,
					flowName: flowId,
				}),
				toPairs(data)
			)
		);
		try {
			for (const [key, value] of Object.entries(data)) {
				if (keysToBeUploaded.includes(key) && !!value) {
					await axios.post(ApiEndpoints.intakeFileUpload!, {
						formUuid: userId,
						fileKey: key,
						fileContent: value,
					});
				}
			}
		} catch (e) {
			setErrorMessage(
				"We've run into a problem. Please refresh the page and try submitting again."
			);
		}

		try {
			await createFlowResponses({
				variables: {
					input: flowResponses,
				},
			});
			setErrorMessage('');
		} catch (e) {
			setErrorMessage(
				"We've run into a problem. Please refresh the page and try submitting again."
			);
			window.parent.scrollTo(0, 0);
			throw e;
		}
		setFlowData({ ...flowData, ...data });
	};

	return {
		loaded: ready,
		error: errorMessage,
		formData: flowData,
		submitFormData,
	};
};

export default useFlowData;
